import { useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { IconCalendarTime, IconMoodSad } from '@tabler/icons-react';
import { useEnrichedMedia } from '../hooks/useEnrichedMedia.ts';
import { groupByMonth } from '../lib/timeline.ts';
import { Media } from '../components/media/Media.tsx';
import { MediaViewer } from '../components/media/MediaViewer.tsx';
import { SkeletonGrid } from '../components/ui/Skeleton.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';

export function TimelinePage() {
  // Timeline groups by each item's filename timestamp — no EXIF batch needed.
  const { items, exifMap, isLoading, isError } = useEnrichedMedia(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // Flat, newest-first ordering matches the buckets so the viewer indexes line up.
  const buckets = useMemo(() => groupByMonth(items), [items]);
  const flat = useMemo(() => buckets.flatMap((b) => b.items), [buckets]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-fg">Timeline</h1>
        <p className="mt-1 text-sm text-fg-2">
          Every photo by capture date, newest first.
        </p>
      </div>

      {isLoading && <SkeletonGrid count={12} />}

      {isError && (
        <EmptyState icon={IconMoodSad} title="Couldn't load the timeline" />
      )}

      {!isLoading && !isError && flat.length === 0 && (
        <EmptyState icon={IconCalendarTime} title="No photos yet" description="Upload some to build your timeline." />
      )}

      {buckets.map((bucket) => (
        <section key={bucket.key} className="space-y-3">
          <h2 className="border border-line bg-surface shadow-sm sticky top-20 z-10 inline-block rounded-md px-3 py-1.5 text-sm font-semibold text-fg-2">
            {bucket.label} · {bucket.items.length}
          </h2>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
            {bucket.items.map((item) => {
              const flatIndex = flat.indexOf(item);
              return (
                <button
                  key={item.key}
                  onClick={() => setViewerIndex(flatIndex)}
                  className="border border-line bg-surface shadow-sm overflow-hidden rounded-lg transition duration-200 ease-standard hover:scale-[1.02] active:scale-[0.97] hover:shadow-lg"
                  aria-label={`Open ${item.displayName}`}
                >
                  <Media
                    fileKey={item.thumbnailKey}
                    kind={item.kind}
                    alt={item.displayName}
                    videoBadge={item.kind === 'video'}
                    className="aspect-square w-full"
                  />
                </button>
              );
            })}
          </div>
        </section>
      ))}

      <AnimatePresence>
        {viewerIndex !== null && flat[viewerIndex] && (
          <MediaViewer
            items={flat}
            index={viewerIndex}
            onIndexChange={setViewerIndex}
            onClose={() => setViewerIndex(null)}
            exifFor={(key) => exifMap?.get(key)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
